import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useAuthContext } from '../../../context/auth.context'
import { lmsAdmin } from '../api'
import { learnerIdKindLabel, shortLearnerId } from '../format'
import { AgoCell, Clip, FAILED_STATE, LmsGrid, PhoneLine, RowActions, agoOf, byField, connectionCell, exportTime, msOf, useGridRows } from '../lms-grid'
import { LmsIcon } from '../lms-icon'
import { useSideBySide } from '../use-side-by-side'
import { ManualLinkModal } from './manual-link-modal'
import { RetireSystemKeyModal } from './retire-system-key-modal'
import { ConfirmDialog } from '../../../shared/sso-workspace/confirm-dialog'
import { useGridList } from '../../../shared/sso-workspace/fleet-grid'
import { Pill } from '../../../shared/sso-workspace/pill'
import { useErrorLine } from '../../../shared/error/use-error-line'
import { useIsMobile } from '../../../shared/hooks/use-is-mobile'
import { TableAgClient } from '../../../shared/table-builder/table-ag-client'
import { ROW_HEIGHT_TWO_LINE } from '../../../shared/table-builder/table-ag'
import { ColumnFormat, ColumnMenu } from '../../../shared/table-builder/table-columns'
import { IconCircleExclamation, IconLink } from '../../../shared/icon/icon'
import { MenuItem } from '../../../shared/menu/menu'
import '../lms-admin.scss'
import { LmsTrail, lmsTrail } from '../lms-trail'
import { SECTION } from '../../../shared/navigation/sections'

// Account Linking (docs/08 §10, design edxr-account-linking-v1.0.html).
// v1 renders link ROWS (linked accounts since Phase F E1b: the LMS's own, in
// the id space lms:<kind>:<issuer>, and those in a partner's system key a
// connection names) — the artifact's per-event delivery/failure feed is
// audit-log material and lives on the Activity Feed surface (deviations
// D-045). Cursor pagination via Load more; stats are computed over the
// loaded rows. A link has the statuses of a linked account (active, suspended,
// disputed, revoked); "Unlink everyone from one LMS" revokes every link in one source.

// The desktop columns' least widths (px). With the Clip sizes in the cells
// (s: the measuring copy capped at 9rem, lms-admin.scss) the four columns fit
// at 1280 beside the sidebar and the pinned Archive column (about 720 of
// 800 px); spare width is then shared out among them.
export const LINK_COLUMN_MIN_WIDTH = { lmsUser: 160, edxrUser: 160, connection: 170 }

// The words of a linked account's status; only an active link is "ok".
const STATUS_WORD = { active: 'Active', suspended: 'Suspended', disputed: 'Disputed', revoked: 'Revoked' }
const statusWord = status => STATUS_WORD[status] || status

// A partner's system key is a slug; the LMS's own space (lms:<kind>:<issuer>)
// is what the id's kind and the connection already say.
const partnerKey = link => (link.system_key && !link.system_key.startsWith('lms:') ? link.system_key : '')

const monthStart = () => {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), 1)
}

export const ViewAccountLinks = () => {
  const authContext = useAuthContext()

  const [connections, updateConnections] = useState([])
  const [connectionsError, updateConnectionsError] = useState(null)
  const [connectionFilter, updateConnectionFilter] = useState('')
  const [showManualLink, updateShowManualLink] = useState(false)
  const [retiring, updateRetiring] = useState(false)
  const [retired, updateRetired] = useState(null)
  const [archiving, updateArchiving] = useState(null)
  const [archiveBusy, updateArchiveBusy] = useState(false)
  const [archiveError, archiveFailure, setArchiveError] = useErrorLine()
  const tableRef = useRef()
  const mobile = useIsMobile()
  // a how-it-works arrow shows only while the next step sits beside its step (measured)
  const [stripRef, stepsBeside] = useSideBySide('.flow-step')
  const stepClass = index => `flow-step${stepsBeside[index] ? ' lms-flow-next' : ''}`

  // The links, a page at a time (useGridRows): the grid's ready (and its
  // footer's reload) reads the first page of the connection chosen above it,
  // choosing another connection reads again server-side, and the cursor's Load
  // more adds the next page. A slow answer for a connection chosen before is
  // dropped.
  const { rows: links, error, next: nextCursor, current, read: load, ready, loadMore } = useGridRows(
    (connectionId, cursor) =>
      lmsAdmin.listLinks({ connectionId, cursor }).then(result => ({ rows: result.links || [], next: result.next_cursor || '' })),
    { filter: connectionFilter }
  )

  useEffect(() => {
    lmsAdmin
      .listConnections()
      .then(result => updateConnections(result.connections || []))
      .catch(updateConnectionsError)
  }, [])

  const connectionsById = useMemo(() => {
    const map = {}
    connections.forEach(connection => {
      map[connection.id] = connection
    })
    return map
  }, [connections])

  const stats = useMemo(() => {
    const all = links || []
    const active = all.filter(link => link.status === 'active')
    const start = monthStart()
    return {
      total: active.length,
      newThisMonth: active.filter(link => new Date(link.established_at) >= start).length,
      connections: new Set(active.map(link => link.established_via_connection_id)).size,
      revoked: all.filter(link => link.status === 'revoked').length
    }
  }, [links])

  // returnFocus (RowActions) puts focus back on the link's row when the dialog closes
  const archiveFocus = useRef(null)
  const askArchive = (link, returnFocus) => {
    setArchiveError(null)
    archiveFocus.current = returnFocus
    updateArchiving(link)
  }
  // Closing the dialog: Cancel returns focus at once; an archive once the list
  // has been read again (`after`), so a slow reload cannot outlast the hold.
  const closeArchive = async after => {
    const returnFocus = archiveFocus.current
    archiveFocus.current = null
    updateArchiving(null)
    if (after) await after()
    if (returnFocus) returnFocus()
  }

  // Archive asks first (what it does to the learner), and a refusal keeps the
  // dialog open with the reason.
  const archive = async () => {
    updateArchiveBusy(true)
    setArchiveError(null)
    try {
      await lmsAdmin.archiveLink(archiving.id)
      await closeArchive(load)
    } catch (e) {
      setArchiveError(e.message || "The link couldn't be archived.", e)
    }
    updateArchiveBusy(false)
  }

  // The cells' words, flattened onto the row: the grid sorts, quick-filters
  // and exports the cell VALUE, so each column's field holds what it shows.
  const gridRows = useMemo(
    () =>
      Array.isArray(links)
        ? links.map(link => {
          const connection = connectionsById[link.established_via_connection_id]
          const kind = learnerIdKindLabel(link.lms_user_id_kind)
          return {
            ...link,
            // Status sits under the time, so the grid's filter finds it in this value
            linkedText: `${agoOf(link.established_at)} ${statusWord(link.status)}`,
            linkedMs: msOf(link.established_at),
            connectionName: connection ? connection.name : `connection ${link.established_via_connection_id}`,
            connectionPlatform: connection && connection.preset ? connection.preset.name : '',
            lmsUserText: [link.lms_user_id, kind, link.lms_user_name_cached, partnerKey(link)].filter(Boolean).join(' '),
            kindLabel: kind,
            partnerKey: partnerKey(link),
            edxrUserText: `${link.edxr_user_email || `user ${link.edxr_user_id}`} user #${link.edxr_user_id}`,
            statusText: statusWord(link.status),
            statusTitle: link.status === 'revoked' ? link.revoke_reason || undefined : undefined,
            statusTone: link.status === 'active' ? 'ok' : null,
            phone: mobile
          }
        })
        : links,
    [links, connectionsById, mobile]
  )
  const gridList = useGridList(gridRows)

  const customer = authContext.state.organizationName

  // The card's menu (owner 2026-10-29): Manually link pinned (kept on a phone),
  // Unlink everyone from one LMS in the ⋮.
  const menu = [
    <MenuItem key="link" Icon={IconLink} text="Manually link" pin keepOnMobile show onClick={() => updateShowManualLink(true)} />,
    <MenuItem key="retire" Icon={IconCircleExclamation} text="Unlink everyone from one LMS" danger pin={false} show onClick={() => updateRetiring(true)} />
  ]

  return (
    <div className="lms-admin" data-testid="lms-account-links">
      <LmsTrail trail={lmsTrail(SECTION.lmsAccountLinks)} />
      <div className="page-header">
        <div className="page-header-text">
          <h1 className="page-header-title">
            Account Linking
            <span className="page-header-customer">
              <LmsIcon name="building" />
              {customer}
            </span>
          </h1>
          <p className="page-header-sub">
            LMS accounts link to EducationXR users automatically on first launch — no IdP, no claim mapping, nothing
            for your organization to configure.
          </p>
        </div>
      </div>

      {retired && (
        <div className="alert alert-success" role="status" data-testid="retire-result">
          {`${retired.links_revoked} ${retired.links_revoked === 1 ? 'account was' : 'accounts were'} unlinked from ${retired.name || retired.system_key}.`}
        </div>
      )}

      {error && (
        <div className="alert alert-danger" role="alert">
          {`Couldn't load account links: ${error.message}`}
        </div>
      )}

      <div className="lms-card" style={{ marginBottom: 16 }}>
        {/* An arrow is drawn in the gap after its step (lms-admin.scss), shown
            while the next step sits beside it. */}
        <div ref={stripRef} className="link-flow">
          <div className={stepClass(0)}>
            <div className="step-title">
              <span className="step-num">1</span>LMS user clicks course
            </div>
            <div className="step-desc">The LMS launches the SCORM package and provides the student id.</div>
            <FlowArrow />
          </div>
          <div className={stepClass(1)}>
            <div className="step-title">
              <span className="step-num">2</span>Launcher delivers signed payload
            </div>
            <div className="step-desc">Deep link on mobile; QR code on desktop for the headset or phone camera.</div>
            <FlowArrow />
          </div>
          <div className={stepClass(2)}>
            <div className="step-title">
              <span className="step-num">3</span>EducationXR validates
            </div>
            <div className="step-desc">The backend verifies the HMAC signature against the connection's signing key.</div>
            <FlowArrow />
          </div>
          <div className={stepClass(3)}>
            <div className="step-title">
              <span className="step-num">4</span>Match or create user
            </div>
            <div className="step-desc">
              Looks up the LMS user id within that LMS — links the existing user or auto-creates one.
            </div>
          </div>
        </div>
        <div className="link-flow-foot" data-testid="link-trust-note">
          <LmsIcon name="shield-halved" />
          <span>
            <strong>Trust mechanism:</strong> HMAC-SHA256 signature on every launch payload, with each connection's own
            signing key (see Package key on the connection's page). No third-party IdP, no claim mapping.
          </span>
        </div>
      </div>

      <div className="summary-row">
        <div className="summary-stat">
          <div className="stat-label">Total linked</div>
          <div className="stat-value" data-testid="stat-total">{stats.total}</div>
          <div className="stat-sub">across all LMS connections</div>
        </div>
        <div className="summary-stat">
          <div className="stat-label">New this month</div>
          <div className="stat-value">{stats.newThisMonth}</div>
          <div className="stat-sub">auto-created or linked on launch</div>
        </div>
        <div className="summary-stat">
          <div className="stat-label">Connections</div>
          <div className="stat-value">{stats.connections}</div>
          <div className="stat-sub">producing linked accounts</div>
        </div>
        <div className="summary-stat">
          <div className="stat-label">Revoked</div>
          <div className="stat-value">{stats.revoked}</div>
          <div className="stat-sub">re-link on next launch</div>
        </div>
      </div>

      <div className="filter-strip">
        <select
          className="form-select form-select-sm"
          style={{ width: 'auto' }}
          value={connectionFilter}
          data-testid="connection-filter"
          aria-label="Connection"
          onChange={e => updateConnectionFilter(e.target.value)}
        >
          <option value="">All connections</option>
          {connections.map(connection => (
            <option key={connection.id} value={connection.id}>
              {connection.name}
            </option>
          ))}
        </select>
        <div className="spacer" />
      </div>

      <LmsGrid
        title="account links"
        menu={menu}
        tableRef={tableRef}
        exportName="lms-account-links"
        after={
          nextCursor && (
            <React.Fragment>
              <span className="sw-hint">{links ? links.length : 0} links loaded; there are more.</span>
              <button
                className="btn btn-sm sw-btn"
                type="button"
                data-testid="load-more"
                // the cursor is the old connection's while the new one is read
                disabled={!current}
                onClick={loadMore}
              >
                Load more
              </button>
            </React.Fragment>
          )
        }>
        <TableAgClient
          ref={tableRef}
          apiList={gridList}
          onReady={ready}
          sortCol="linkedText"
          sortDesc
          rowHeight={ROW_HEIGHT_TWO_LINE}
          emptyState={links === null ? FAILED_STATE : {
            Icon: IconLink,
            title: 'No linked accounts yet',
            message: "Once an LMS user launches a course through one of your SCORM packages, the link shows up here. There's nothing to set up — linking happens automatically on first launch."
          }}
          noMatchState={{ message: 'No links match. Clear the search.' }}>
          <ColumnFormat
            field="lmsUserText"
            header="LMS user"
            render={lmsUserCell}
            exportValue={exportLmsUser}
            stacked
            minWidth={mobile ? 150 : LINK_COLUMN_MIN_WIDTH.lmsUser}
            flex={2}
            sortable
          />
          <ColumnFormat
            field="edxrUserText"
            header="EducationXR user"
            render={edxrUserCell}
            exportValue={exportEdxrUser}
            stacked
            minWidth={LINK_COLUMN_MIN_WIDTH.edxrUser}
            flex={2}
            sortable
            hide={mobile}
          />
          <ColumnFormat field="connectionName" header="LMS connection" render={connectionCell} stacked minWidth={LINK_COLUMN_MIN_WIDTH.connection} flex={1} sortable hide={mobile} />
          <ColumnFormat
            field="linkedText"
            header="Linked"
            render={linkedCell}
            exportValue={exportLinked}
            comparator={byField('linkedMs')}
            stacked
            sortable
            hide={mobile}
          />
          <ColumnMenu
            menu={link => (
              <RowActions
                rowId={link.id}
                rowName={shortLearnerId(link.lms_user_id)}
                actions={[{ label: 'Archive', name: `Archive ${shortLearnerId(link.lms_user_id)}`, danger: true, show: link.status === 'active', onClick: returnFocus => askArchive(link, returnFocus) }]}
              />
            )}
          />
        </TableAgClient>
      </LmsGrid>

      {retiring && (
        <RetireSystemKeyModal
          connections={connections}
          connectionsError={connectionsError}
          onClose={() => updateRetiring(false)}
          onRetired={async (result, name) => {
            updateRetiring(false)
            updateRetired({ ...result, name })
            await load()
          }}
        />
      )}

      {showManualLink && (
        <ManualLinkModal
          connections={connections}
          onClose={() => updateShowManualLink(false)}
          onCreated={async () => {
            updateShowManualLink(false)
            await load()
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(archiving)}
        title={archiving ? `Archive the link for LMS user "${shortLearnerId(archiving.lms_user_id)}"?` : ''}
        body="This removes their LMS learner-group membership — the learner will be asked to re-link on their next launch."
        confirmText="Archive link"
        busy={archiveBusy}
        error={archiveError}
        failure={archiveFailure}
        onConfirm={archive}
        onCancel={() => closeArchive()}
      />
    </div>
  )
}

// The arrow from a how-it-works step to the next.
const FlowArrow = () => (
  <span className="flow-arrow">
    <LmsIcon name="arrow-right" />
  </span>
)

// The CSV values of the merged columns: the LMS user is its id exactly as stored
// (the whole id, not the start the cell shows), the kind of an id that is not
// SCORM and the name the LMS gave; the EducationXR user is the email (or
// "user N") and the number; Linked is the precise UTC time and the status.
export const exportLmsUser = link => [link.lms_user_id, link.kindLabel, link.lms_user_name_cached].filter(Boolean).join(' · ')
export const exportEdxrUser = link => `${link.edxr_user_email || `user ${link.edxr_user_id}`} (user #${link.edxr_user_id})`
export const exportLinked = link => `${exportTime(link.established_at)} · ${link.statusText}`

// The LMS's learner id exactly as stored (its start, when it runs long, and the
// whole id on hover), over the kind of an id that is not SCORM and the name the
// LMS gave; on a phone the status pill and the EducationXR learner instead, in
// place of the columns a phone has no room for.
const lmsUserCell = cell => {
  const link = cell.data
  if (!link) return ''
  // Its children are not one string, so Clip cannot take the hover text from
  // them: the whole line is given (Task 21 carry).
  const detailText = [link.kindLabel, link.lms_user_name_cached, link.partnerKey].filter(Boolean).join(' · ')
  const detail = detailText && (
    <Clip size="s" className="sw-cell-sub" title={detailText}>
      {link.kindLabel && <span data-testid={`link-kind-${link.id}`}>{link.kindLabel}</span>}
      {link.kindLabel && link.lms_user_name_cached && ' · '}
      {link.lms_user_name_cached}
      {link.partnerKey && (link.kindLabel || link.lms_user_name_cached) && ' · '}
      {link.partnerKey && <span data-testid={`link-system-key-${link.id}`}>{link.partnerKey}</span>}
    </Clip>
  )
  return (
    <span className="min-w-0">
      <Clip size="s" className="sw-mono" title={link.lms_user_id}>{shortLearnerId(link.lms_user_id)}</Clip>
      {link.phone
        ? <PhoneLine pill={<Pill tone={link.statusTone} title={link.statusTitle}>{link.statusText}</Pill>}>{link.edxr_user_email || `user ${link.edxr_user_id}`}</PhoneLine>
        : detail}
    </span>
  )
}

// The EducationXR person the id is linked to.
const edxrUserCell = cell => {
  const link = cell.data
  if (!link) return ''
  const who = link.edxr_user_email || `user ${link.edxr_user_id}`
  return (
    <span className="min-w-0">
      <Clip size="s" className="fw-semibold">{who}</Clip>
      <span className="sw-cell-sub">user #{link.edxr_user_id}</span>
    </span>
  )
}

// How long ago the link was made, over its status.
const linkedCell = cell => {
  const link = cell.data
  if (!link) return ''
  return (
    <span className="min-w-0">
      <span><AgoCell value={link.established_at} /></span>
      <span><Pill tone={link.statusTone} title={link.statusTitle}>{link.statusText}</Pill></span>
    </span>
  )
}
